function isArrayEqual(source, target, options) {
	const {length: sourceLength} = source;
	const {length: targetLength} = target;

	if (sourceLength !== targetLength) return false;

	for (let i = 0; i < sourceLength; i++) {
		if (!isEqual(source[i], target[i], options)) return false;
	}

	return true;
}

function isObjectEqual(source, target, options) {
	if ((!source && target) || (source && !target)) return false;

	const sourceKeys = Object.keys(source);
	const targetKeys = Object.keys(target);
	const {length: sourceKeyLength} = sourceKeys;
	const {length: targetKeyLength} = targetKeys;

	if (sourceKeyLength !== targetKeyLength) return false;

	return sourceKeys.every(key => isEqual(source[key], target[key], options));
}

function isObjectTagEqual(source, target, options) {
	const {classes = true} = options;
	const sourceTag = Object.prototype.toString.call(source);
	const targetTag = Object.prototype.toString.call(target);

	if (sourceTag !== targetTag) return false;

	switch (sourceTag) {
		case '[object Boolean]':
		case '[object Number]':
		case '[object Date]':
			return eq(+source, +target);

		case '[object Set]':
			return isArrayEqual([...source], [...target], options);

		case '[object Map]':
			return isObjectEqual(Object.fromEntries(source), Object.fromEntries(target), options);

		case '[object String]':
			return `${source}` === `${target}`;

		case '[object Object]':
			if (!classes && source.constructor !== Object) return source === target;
			return isObjectEqual(source, target, options);
	}
}

export const eq = function (source, target) {
	// Given:
	// const source = { 'a': 1 };
	// const target = { 'a': 1 };
	//
	// Results:
	// eq(source, source)  => true
	// eq(source, target) => false
	// eq('a', 'a') => true
	// eq('a', Object('a')) => false
	// eq(NaN, NaN) => true
	return source === target || (source !== source && target !== target);
};

/**
 * Performs a deep equality check between two values.
 *
 * @example
 * const foo = { a: 1 };
 * const bar = { a: 1 };
 *
 * isEqual(foo, bar);
 * // => true
 *
 * foo === bar
 * // => false
 *
 * @function isEqual
 * @param {*} source The value to compare.
 * @param {*} target The value to compare `source` against.
 * @return {boolean} Returns `true` if the values are equal, otherwise `false`.
 */
export default function isEqual(source, target, options = {}) {
	if (source === target) return true;

	if (Array.isArray(source) && Array.isArray(target)) return isArrayEqual(source, target, options);

	const result = isObjectTagEqual(source, target, options);
	if (result !== undefined) return result;

	if (typeof source === 'object' && typeof target === 'object')
		return isObjectEqual(source, target, options);

	return typeof source === 'function' && typeof target === 'function' && source === target;
}
